<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { computed } from 'vue';
import ResourceDialogQualificationStatus from './ResourceDialogQualificationStatus.vue';
import { useUserStore } from '/@stores/user';
import { Qualification } from '/@types/qualification';
import { ResourceFilter } from '/@types/resources';
import { RolePermission } from '/@types/user';
import http from '/@utilities/http';

const { resource } = defineProps<{
  resource: {
    name: string;
    filters: Array<{
      id: number;
      name: string;
      type: string;
    }>;
    qualifications: {
      id: number;
      name: string;
      statusId: number;
    }[];
  };
  resourceFilters: ResourceFilter[];
  close: Function;
}>();

const userStore = useUserStore();

const userHasQualificationsPermission = computed(() =>
  userStore.hasRolePerm(RolePermission.QualificationUser),
);

const filteredQualifications = computed(() =>
  resource.qualifications
    ? [...resource.qualifications.values()]
        .filter((x) => qualifications.value?.some((y) => y.Id === x.id && y.IsAvailableInResource))
        .sort((a, b) => a.name.localeCompare(b.name, 'nb-no'))
    : [],
);

const { data: qualifications } = useQuery<Qualification[]>({
  queryFn: () => http.get(`/qualifications`).then((response) => response.data),
  queryKey: ['qualifications'],
  enabled: userHasQualificationsPermission.value,
});
</script>

<template>
  <div class="dialog">
    <header class="head">
      <df-button v-on:click="close()">
        <template v-slot:icon>
          <df-icon code="f060" />
        </template>
      </df-button>

      <h1 class="title-md">{{ resource.name }}</h1>
    </header>

    <dl class="dl card" style="padding: 0">
      <div class="dl__group dl__group--large">
        <dt class="dl__topic">Grupper</dt>
        <dd class="dl__description">
          <ul class="list">
            <li v-for="filter in resource.filters" :key="filter.id" class="list__item">
              <template
                v-for="f in resourceFilters.filter(({ id }) => id === filter.id)"
                :key="f.id"
              >
                {{ f.name }}
                <div class="badge" v-if="f.type">{{ f.type }}</div>
                <div class="badge badge--info" v-if="f.isUserSpecific">Personlig</div>
              </template>
            </li>
          </ul>
        </dd>
      </div>
      <div v-if="filteredQualifications.length > 0" class="dl__group dl__group--large">
        <dt class="dl__topic">Kompetanser</dt>
        <dd class="dl__description">
          <ul class="list">
            <li
              v-for="qualification in filteredQualifications"
              :key="qualification.id"
              class="list__item"
            >
              <div class="info">
                <span>
                  {{ qualification.name }}
                </span>
                <ResourceDialogQualificationStatus :status-id="qualification.statusId" />
              </div>
            </li>
          </ul>
        </dd>
      </div>
    </dl>
  </div>
</template>

<style scoped>
.list__item {
  margin: var(--gap-sm) 0;
}

.info {
  display: flex;
  gap: var(--gap-md);
}
</style>
